import React, { useMemo, useCallback } from "react";

import * as FormHelpers from "../forms/form-helpers";
import { StaffMember } from "../../hooks/use-user";
import { useReferenceData } from "../../hooks/use-reference-data";
import { SortableTable, SortableTableHeader } from "../table";
import { useDynamicPageSize } from "../table/use-dynamic-page-size";
import { useAccessControl } from "../../app/use-access-control";

export interface StaffSelectorProps {
  staff: StaffMember[] | undefined;
  selected?: StaffMember | undefined;
  onSelect?: (staff: StaffMember) => void;
  filter: string;
}

// Memoized avatar component to prevent unnecessary re-renders
const StaffAvatar = React.memo(({ photoUrl }: { photoUrl: string | undefined }) => (
  <div className="avatar">
    <img src={photoUrl || "/img/icon-placeholder-leaves.svg"} alt="" loading="lazy" />
  </div>
));

StaffAvatar.displayName = "StaffAvatar";

export const StaffSelector = React.forwardRef<HTMLTableElement, StaffSelectorProps>(
  ({ staff, selected, onSelect, filter }, tableRef) => {
    const { positionsLookup } = useReferenceData();
    const { hasFeatureAccess } = useAccessControl();

    const nameFilteredStaff = useMemo(() => {
      return staff?.filter(s => s.displayName.toLowerCase().includes(filter.toLowerCase())) ?? [];
    }, [staff, filter]);

    const { paginationOptions } = useDynamicPageSize({
      utilityHeight: 145,
      tableRowHeight: 65,
      tablePadding: 0,
      containerPadding: 0
    });

    const onSelectItem = useCallback(
      (item: StaffMember) => {
        onSelect?.(item);
      },
      [onSelect]
    );

    const staffListHeaders: SortableTableHeader<StaffMember>[] = useMemo(
      () => [
        {
          renderKey: "displayName",
          name: "Staff",
          tableDataCellProps: { isPrimary: true },
          sortOptions: { enableSort: true },
          render: (item: StaffMember) => (
            <div className="listing-avatar">
              {hasFeatureAccess?.("staff_photo") && <StaffAvatar photoUrl={item.photoUrl} />}
              <div>
                <span className="staff-name">{item.displayName}</span>
                <br />
                <span className="secondary">{item.position ? positionsLookup[item.position] : ""}&nbsp;</span>
              </div>
            </div>
          )
        },
        {
          renderKey: "businessEmail",
          name: "Email/Phone",
          render: (item: StaffMember) => (
            <>
              {item.businessEmail.toLowerCase()}
              <br />
              {FormHelpers.formatPhoneNumber(item.mobilePhone)}
            </>
          )
        },
        {
          key: "staffId",
          name: "LCOS 1.0 Staff ID",
          sortOptions: { enableSort: true }
        }
      ],
      [positionsLookup]
    );

    return (
      <SortableTable
        tableCollapseScreenSize="sm"
        headers={staffListHeaders}
        data={nameFilteredStaff}
        activeItem={selected}
        paginationOptions={paginationOptions}
        onRowClick={onSelectItem}
        tableRef={tableRef}
        isLoading={false}
        uniqueIdKey="id"
        printCaption="Staff"
      />
    );
  }
);

StaffSelector.displayName = "StaffSelector";
